import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BRAND } from '../config';
import { QRCodeSVG } from 'qrcode.react';

const Hostess = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessionInfo, setSessionInfo] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', play_mode: 'whatsapp' });
  const [qtyPacks, setQtyPacks] = useState(0);
  const [ticketPhoto, setTicketPhoto] = useState(null);
  const [ticketPreview, setTicketPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [gameToken, setGameToken] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState(null);

  const fileInputRef = useRef(null);

  const loadSessionInfo = useCallback(async () => {
    try {
      const response = await api.get('/hostess/active-session', { params: { brand: BRAND } });
      setSessionInfo(response.data);
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors du chargement de la session' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessionInfo();
  }, [loadSessionInfo]);

  const handleSubmit = async (mode) => {
    // Validation: nom obligatoire
    if (!formData.name) {
      setMessage({ type: 'danger', text: 'Veuillez remplir le nom' });
      return;
    }

    const phoneDigits = (formData.phone || '').replace(/\D/g, '');
    if (!phoneDigits) {
      setMessage({ type: 'danger', text: 'Veuillez remplir le numéro de téléphone' });
      return;
    }
    if (!/^[267]\d{8}$/.test(phoneDigits)) {
      setMessage({ type: 'danger', text: 'Numéro invalide. Format attendu: 6XXXXXXXX (9 chiffres) (Cameroun).' });
      return;
    }
    const normalizedPhone = `+237${phoneDigits}`;

    // Validation: au moins 3 packs 1.5L
    if (qtyPacks < 3) {
      setMessage({ type: 'danger', text: 'Minimum 3 packs Supermont 1.5L requis pour participer' });
      return;
    }

    // Validation: photo du ticket obligatoire
    if (!ticketPhoto) {
      setMessage({ type: 'danger', text: 'Veuillez prendre une photo du ticket de caisse' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    const formDataObj = new FormData();
    formDataObj.append('name', formData.name);
    formDataObj.append('phone', normalizedPhone);
    formDataObj.append('play_mode', mode);
    formDataObj.append('qty_packs', qtyPacks);
    formDataObj.append('brand', BRAND);
    if (ticketPhoto) {
      formDataObj.append('ticket_photo', ticketPhoto);
    }

    try {
      const response = await api.post('/hostess/register-participant', formDataObj);

      const token = response.data.game_token;

      if (mode === 'whatsapp') {
        const link = response.data.whatsapp_link;

        setMessage({ type: 'success', text: 'Participant enregistré !' });

        // Reset du formulaire
        setFormData({ name: '', phone: '', play_mode: 'whatsapp' });
        setQtyPacks(0);
        setTicketPhoto(null);
        setTicketPreview(null);
        loadSessionInfo();

        // Ouvrir WhatsApp (window.location.href n'est pas bloqué par les navigateurs)
        if (link) {
          window.location.href = link;
        }
      } else if (mode === 'qr_code') {
        if (token) {
          setGameToken(token);
          setShowQRCode(true);
        } else {
          setMessage({ type: 'danger', text: 'Erreur: token de jeu non généré' });
        }
      } else {
        // Mode Direct
        if (!token) {
          setMessage({ type: 'danger', text: 'Erreur: token de jeu non généré' });
          return;
        }
        setMessage({ type: 'success', text: 'Participant enregistré ! Redirection vers le jeu...' });
        setTimeout(() => {
          navigate(`/game/${token}`);
        }, 500);
        return;
      }
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de l\'enregistrement' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCloseQRCode = () => {
    setShowQRCode(false);
    setGameToken(null);
    setWhatsappLink(null);
    setFormData({ name: '', phone: '', play_mode: 'whatsapp' });
    setQtyPacks(0);
    setTicketPhoto(null);
    setTicketPreview(null);
    loadSessionInfo();
  };

  useEffect(() => {
    if (!showQRCode || !gameToken) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/game/result/${gameToken}`);
        if (res.data.token_opened || res.data.played) {
          handleCloseQRCode();
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [showQRCode, gameToken]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setTicketPhoto(file);
      setTicketPreview(URL.createObjectURL(file));
    }
  };

  const clearPhoto = () => {
    if (window.confirm('Supprimer cette photo ?')) {
      setTicketPhoto(null);
      setTicketPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f4f6f9' }}>
        <div className="text-center" style={{ color: '#1565c0' }}>
          <div className="mb-4">
            <div style={{ width: '60px', height: '60px', margin: '0 auto', position: 'relative' }}>
              <div className="spinner-border" role="status" style={{ width: '100%', height: '100%', borderWidth: '4px', color: '#1565c0' }}></div>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <i className="fas fa-gem" style={{ fontSize: '20px', color: '#1565c0' }}></i>
              </div>
            </div>
          </div>
          <p className="mb-0 fw-bold" style={{ fontSize: 'clamp(14px, 3vw, 18px)', color: '#333' }}>CHARGEMENT...</p>
        </div>
      </div>
    );
  }

  if (!sessionInfo || !sessionInfo.active) {
    return (
      <div className="min-vh-100 d-flex flex-column" style={{ background: '#f4f6f9' }}>
        {/* Navbar bleue */}
        <nav className="navbar navbar-expand-lg navbar-dark" style={{ background: '#1565c0', padding: '8px 0' }}>
          <div className="container d-flex justify-content-center">
            <a className="navbar-brand fw-bold d-flex align-items-center gap-2" href="/">
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#ffffff', overflow: 'hidden', padding: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="/images/logo-supermont.png" alt="Supermont" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }} />
              </div>
              <span>Les Perles de Vie</span>
            </a>
          </div>
          <div className="container d-flex justify-content-end" style={{ position: 'absolute', top: '12px', right: '16px' }}>
            <span className="text-white me-2 d-none d-sm-inline">
              <i className="fas fa-user-tag me-1"></i>{user?.username}
            </span>
            <button onClick={handleLogout} className="btn btn-outline-light btn-sm" style={{ fontSize: '13px' }}>
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </nav>

        <div className="container flex-grow-1 d-flex align-items-center py-3">
          <div className="card border-0 shadow w-100" style={{ borderRadius: '16px', background: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div className="card-body py-4 py-md-5 text-center">
              <div style={{ width: '70px', height: '70px', background: '#e3f2fd', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <i className="fas fa-calendar-times" style={{ fontSize: '28px', color: '#1565c0' }}></i>
              </div>
              <h3 className="mb-2 fw-bold" style={{ fontSize: 'clamp(18px, 4vw, 24px)', color: '#1a1a1a' }}>Aucune session active</h3>
              <p style={{ fontSize: 'clamp(13px, 2.5vw, 15px)', color: '#555' }} className="mb-3">
                Il n'y a pas de session de jeu active pour votre point de vente.<br />
                Veuillez contacter un administrateur.
              </p>
              <button onClick={loadSessionInfo} className="btn btn-lg px-4" style={{ background: '#1565c0', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: 'clamp(14px, 2.5vw, 16px)', borderRadius: '10px' }}>
                <i className="fas fa-sync-alt me-2"></i>Actualiser
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: '#f4f6f9' }}>
      {/* Header — reste bleu */}
      <nav className="navbar navbar-expand-lg navbar-dark sticky-top" style={{ background: '#1565c0', padding: '8px 0', position: 'relative' }}>
        <div className="container d-flex justify-content-center">
          <a className="navbar-brand fw-bold d-flex align-items-center gap-2" href="/">
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#ffffff', overflow: 'hidden', padding: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src="/images/logo-supermont.png" alt="Supermont" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }} />
            </div>
            <span style={{ fontSize: '16px' }}>Les Perles de Vie</span>
          </a>
        </div>
        <div className="container d-flex justify-content-end" style={{ position: 'absolute', top: '8px', right: '16px' }}>
          <span className="text-white me-2 d-none d-md-block">
            <small style={{ fontSize: '10px', display: 'block' }}>Connecté en tant que</small>
            <strong style={{ fontSize: '13px' }}><i className="fas fa-user-tag me-1"></i>{user?.username}</strong>
          </span>
          <button onClick={handleLogout} className="btn btn-outline-light btn-sm" style={{ padding: '6px 10px', fontSize: '13px' }}>
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </nav>

      <div className="container py-3 py-md-4">
        {message.text && (
          <div className={'alert alert-' + (message.type === 'success' ? 'success' : 'danger') + ' alert-dismissible fade show mb-3 shadow-sm'} role="alert" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
            <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'} me-2`}></i>
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        {/* Session Info Bar */}
        <div className="card border-0 mb-3" style={{ borderRadius: '12px', background: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div className="card-body py-2 py-md-3">
            <div className="row align-items-center g-2 g-md-3">
              <div className="col-12 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: '#1565c0', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-calendar-check" style={{ color: '#fff', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666', display: 'block' }}>Session</small>
                    <strong className="d-block" style={{ fontSize: '13px', color: '#1a1a1a' }}>{sessionInfo.session_name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: '#e3f2fd', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-store" style={{ color: '#1565c0', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666', display: 'block' }}>PDV</small>
                    <strong className="d-block" style={{ fontSize: '13px', color: '#1a1a1a' }}>{sessionInfo.pdv_name}</strong>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-4">
                <div className="d-flex align-items-center">
                  <div style={{ width: '36px', height: '36px', background: '#e3f2fd', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-clock" style={{ color: '#1565c0', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <small style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666', display: 'block' }}>Horaires</small>
                    <strong className="d-block" style={{ fontSize: '11px', color: '#1a1a1a' }}>
                      {new Date(sessionInfo.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {new Date(sessionInfo.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-3 g-md-4">
          {/* Registration Form */}
          <div className="col-12">
            <div className="card border-0" style={{ borderRadius: '12px', background: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center mb-3">
                  <div style={{ width: '36px', height: '36px', background: '#1565c0', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
                    <i className="fas fa-user-plus" style={{ color: '#fff', fontSize: '14px' }}></i>
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold" style={{ fontSize: 'clamp(14px, 2.5vw, 16px)', color: '#1a1a1a' }}>Nouveau Participant</h6>
                    <small style={{ fontSize: 'clamp(11px, 2vw, 13px)', color: '#555' }}>Enregistrez un client pour le jeu</small>
                  </div>
                </div>

                <form onSubmit={(e) => e.preventDefault()}>
                  {/* Photo Section */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small" style={{ fontSize: 'clamp(11px, 2vw, 13px)', color: '#333' }}>
                      <i className="fas fa-camera me-2" style={{ color: '#1565c0' }}></i>1. Photo du ticket
                    </label>
                    {ticketPreview ? (
                      <div className="position-relative">
                        <div className="card border-2" style={{ borderColor: '#1565c0', background: '#f0f6ff' }}>
                          <div className="card-body p-2 text-center">
                            <img
                              src={ticketPreview}
                              alt="Ticket"
                              style={{ maxHeight: '150px', width: '100%', objectFit: 'contain', borderRadius: '8px' }}
                            />
                            <div className="mt-2">
                              <span className="badge" style={{ background: '#1565c0', color: '#fff', fontSize: 'clamp(10px, 2vw, 12px)' }}>
                                <i className="fas fa-check-circle me-1"></i>Photo OK
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={clearPhoto}
                          className="position-absolute top-0 end-0 btn btn-danger btn-sm shadow m-2"
                          title="Supprimer la photo"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="btn w-100 py-2 py-md-3 border-2"
                          style={{ background: '#f0f6ff', borderColor: '#1565c0', color: '#1565c0', fontSize: 'clamp(13px, 2.5vw, 15px)', borderRadius: '8px' }}
                        >
                          <i className="fas fa-camera me-2"></i>
                          <span className="fw-semibold">Prendre une photo</span>
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                      </>
                    )}
                  </div>

                  {/* Informations du participant */}
                  <div className="row g-2 g-md-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small" style={{ fontSize: 'clamp(11px, 2vw, 13px)', color: '#333' }}>2. Nom</label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: '#e3f2fd', borderColor: '#dee2e6', color: '#1565c0', padding: '10px 12px' }}><i className="fas fa-user"></i></span>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Ex: Jean Dupont"
                          required
                          style={{ background: '#fff', borderColor: '#dee2e6', color: '#1a1a1a', fontSize: 'clamp(13px, 2.5vw, 16px)' }}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small" style={{ fontSize: 'clamp(11px, 2vw, 13px)', color: '#333' }}>3. Téléphone</label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ background: '#e3f2fd', borderColor: '#dee2e6', color: '#1565c0', padding: '10px 12px' }}><i className="fas fa-phone"></i></span>
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="6XXXXXXXX (obligatoire)"
                          value={formData.phone}
                          onChange={(e) => {
                            let digits = (e.target.value || '').replace(/\D/g, '');
                            if (digits.startsWith('00237')) digits = digits.slice(5);
                            if (digits.startsWith('237')) digits = digits.slice(3);
                            digits = digits.slice(0, 9);
                            setFormData({ ...formData, phone: digits });
                          }}
                          style={{ background: '#fff', borderColor: '#dee2e6', color: '#1a1a1a', fontSize: 'clamp(13px, 2.5vw, 16px)' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Nombre de bouteilles */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small" style={{ fontSize: 'clamp(11px, 2vw, 13px)', color: '#333' }}>
                      4. Nombre de packs Supermont 1.5L achetés
                    </label>
                    <div className="card border-0" style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '10px' }}>
                      <div className="card-body p-3 text-center">
                        <div className="d-flex align-items-center justify-content-center gap-3">
                          <button type="button" className="btn btn-sm" onClick={() => setQtyPacks(q => Math.max(0, q - 1))}
                            style={{ background: '#e3f2fd', border: '1px solid #1565c0', color: '#1565c0', width: '40px', height: '40px', padding: 0, fontSize: '18px', borderRadius: '8px' }}>
                            <i className="fas fa-minus" style={{ fontSize: '12px' }}></i>
                          </button>
                          <span style={{ fontSize: 'clamp(22px, 4vw, 28px)', minWidth: '40px', color: '#1a1a1a', fontWeight: 'bold' }}>{qtyPacks}</span>
                          <button type="button" className="btn btn-sm" onClick={() => setQtyPacks(q => q + 1)}
                            style={{ background: '#e3f2fd', border: '1px solid #1565c0', color: '#1565c0', width: '40px', height: '40px', padding: 0, fontSize: '18px', borderRadius: '8px' }}>
                            <i className="fas fa-plus" style={{ fontSize: '12px' }}></i>
                          </button>
                        </div>
                        {qtyPacks > 0 && (
                          <div className="mt-2">
                            <span style={{ fontSize: 'clamp(11px, 2vw, 13px)', color: '#555' }}>
                              <i className="fas fa-gem me-1" style={{ color: '#1565c0' }}></i>
                              {qtyPacks} pack{qtyPacks > 1 ? 's' : ''} → <strong>{Math.floor(qtyPacks / 3)}</strong> tour{Math.floor(qtyPacks / 3) > 1 ? 's' : ''} de jeu
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-12">
                      <label className="form-label fw-semibold small" style={{ fontSize: 'clamp(11px, 2vw, 13px)', color: '#333' }}>5. Mode de jeu</label>
                      <div className="row g-2">
                        <div className="col-4">
                          <button
                            type="button"
                            className="btn w-100 py-2 py-md-3"
                            disabled={submitting}
                            style={{
                              background: '#25D366',
                              color: '#fff',
                              border: '2px solid #25D366',
                              fontSize: 'clamp(11px, 2vw, 13px)',
                              fontWeight: 'bold',
                              borderRadius: '10px',
                              opacity: submitting ? 0.6 : 1
                            }}
                            onClick={() => handleSubmit('whatsapp')}
                          >
                            <i className="fab fa-whatsapp me-1"></i>
                            <div className="fw-semibold">WhatsApp</div>
                            <small style={{ fontSize: 'clamp(8px, 1.8vw, 10px)' }}>Envoyer au client</small>
                          </button>
                        </div>
                        <div className="col-4">
                          <button
                            type="button"
                            className="btn w-100 py-2 py-md-3"
                            disabled={submitting}
                            style={{
                              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                              color: '#fff',
                              border: '2px solid #1976d2',
                              fontSize: 'clamp(11px, 2vw, 13px)',
                              fontWeight: 'bold',
                              borderRadius: '10px',
                              opacity: submitting ? 0.6 : 1
                            }}
                            onClick={() => handleSubmit('direct')}
                          >
                            <i className="fas fa-mobile-alt me-1"></i>
                            <div className="fw-semibold">Jouer Direct</div>
                            <small style={{ fontSize: 'clamp(8px, 1.8vw, 10px)' }}>Sur ce mobile</small>
                          </button>
                        </div>
                        <div className="col-4">
                          <button
                            type="button"
                            className="btn w-100 py-2 py-md-3"
                            disabled={submitting}
                            style={{
                              background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                              color: '#fff',
                              border: '2px solid #8B5CF6',
                              fontSize: 'clamp(11px, 2vw, 13px)',
                              fontWeight: 'bold',
                              borderRadius: '10px',
                              opacity: submitting ? 0.6 : 1
                            }}
                            onClick={() => handleSubmit('qr_code')}
                          >
                            <i className="fas fa-qrcode me-1"></i>
                            <div className="fw-semibold">QR Code</div>
                            <small style={{ fontSize: 'clamp(8px, 1.8vw, 10px)' }}>Scanner</small>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCode && gameToken && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '99999',
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 15px'
            }}>
              <i className="fas fa-qrcode" style={{ fontSize: '24px', color: '#fff' }}></i>
            </div>
            <h2 style={{
              color: '#1a1a1a',
              marginBottom: '10px',
              fontSize: 'clamp(18px, 4vw, 24px)',
              fontWeight: 'bold'
            }}>
              QR Code de Jeu
            </h2>
            <p style={{
              color: '#555',
              marginBottom: '20px',
              fontSize: 'clamp(13px, 2.5vw, 15px)'
            }}>
              Le client doit scanner ce QR code pour accéder au jeu Les Perles de Vie
            </p>
            <div style={{
              background: '#fff',
              padding: '20px',
              borderRadius: '15px',
              marginBottom: '20px',
              display: 'inline-block',
              border: '1px solid #dee2e6'
            }}>
              <QRCodeSVG
                value={`${window.location.origin}/game/${gameToken}`}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => window.open(`${window.location.origin}/game/${gameToken}`, '_blank')}
                style={{
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-external-link-alt"></i>
                Tester le lien
              </button>
              <button
                onClick={handleCloseQRCode}
                style={{
                  background: '#f4f6f9',
                  color: '#333',
                  border: '2px solid #dee2e6',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-times me-2"></i>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hostess;
